import React, { useState, useEffect } from 'react';
import {
  ArrowUp,
  Folder,
  Upload,
  FileText,
  Trash2,
  Download,
  RefreshCw,
  Search,
  Sparkles,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Common/button';
import { DocumentContext } from '@/pages/KnowledgeBase/KnowledgeBasePage';
import { documentService, type DocumentRecord, type DocumentVisibility } from '@/services/documentService';
import type { CustomFolder } from '@/services/customFolderService';
import type { KBFolderInfo, ProjectKBInfo } from '@/services/kbService';
import { ROOT_FOLDER_PATH, childFolders, parentFolderPath } from '@/lib/knowledgeFolders';
import { projectService } from '@/services/projectService';
import { indexingService } from '@/services/indexingService';
import notify from '@/lib/notify';
import { apiErrorDetail } from '@/lib/knowledgeSharing';
import { useIsOrganizationAdmin } from '@/stores/authStore';
import {
  DefaultFolderBadge,
  DocumentVisibilityControl,
  PrivateBadge,
  SharedBadge,
} from '@/components/Documents/KnowledgeVisibility';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface DocumentsExplorerProps {
  context: DocumentContext;
  /** Folder record when `context` is a custom folder: drives sharing badges and admin-only uploads. */
  customFolder?: CustomFolder;
  /** Project record when `context` is a project: its files are managed from the project page. */
  project?: ProjectKBInfo;
  /** Subfolders of the domain, use case or solution (all levels), from the Memory structure. */
  folders?: KBFolderInfo[];
  /** Opens a subfolder (or the root, `/`) of the current domain, use case or solution. */
  onOpenFolder?: (folderPath: string) => void;
  onDocumentsChange?: () => void;
}

interface SearchResult {
  id: string;
  content: string;
  score: number;
  document_name: string;
}

interface UploadProgressState {
  total: number;
  completed: number;
}

export const DocumentsExplorer: React.FC<DocumentsExplorerProps> = ({
  context,
  customFolder,
  project,
  folders,
  onOpenFolder,
  onDocumentsChange,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isProjectContext = context.type === 'project';
  const folderPath = context.folderPath ?? ROOT_FOLDER_PATH;
  const subfolders = childFolders(folders, folderPath);
  const dateLocale = i18n.language?.startsWith('fr') ? fr : enUS;
  const isOrganizationAdmin = useIsOrganizationAdmin();
  const MAX_THEME_CHIPS = 3;
  const ACCEPTED_FORMAT_EXTENSIONS = [
    '.pdf',
    '.docx',
    '.doc',
    '.txt',
    '.md',
    '.eml',
    '.html',
    '.htm',
    '.rtf',
    '.pptx',
    '.xlsx',
    '.csv',
    '.tsv',
    '.odt',
    '.ods',
    '.odp',
    '.json',
    '.xml',
  ];
  const acceptedFormatsLabel = ACCEPTED_FORMAT_EXTENSIONS.join(',');
  const [activeViewTab, setActiveViewTab] = useState<'documents' | 'search'>('documents');
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [indexingDoc, setIndexingDoc] = useState<string | null>(null);
  const [indexingAll, setIndexingAll] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressState>({ total: 0, completed: 0 });

  // Load documents when context changes
  useEffect(() => {
    // Reset search interface when context changes
    setSearchQuery('');
    setSearchResults([]);
    setActiveViewTab('documents');

    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.id, context.type, context.folderPath]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      let docs: DocumentRecord[] = [];
      // The backend folder filter is a prefix match (subfolders included): keep this folder's files only.
      const inFolder = (records: DocumentRecord[]) =>
        records.filter((doc) => (doc.folder_path || ROOT_FOLDER_PATH) === folderPath);

      switch (context.type) {
        case 'domain':
          docs = inFolder(await documentService.listForDomain(context.id, { folder_path: folderPath }));
          break;
        case 'cadrage':
          docs = inFolder(await documentService.listForCadrage(context.id, { folder_path: folderPath }));
          break;
        case 'custom_folder':
          docs = await documentService.listForCustomFolder(context.id);
          break;
        case 'use_case':
          // The use case's own files; its solutions' files are listed under each solution.
          docs = inFolder(await documentService.listByAudit(context.id, { folder_path: folderPath }));
          break;
        case 'project': {
          // Current version only; kept zips and files that cannot be indexed never reach the KB.
          const files = await projectService.currentFiles(context.id);
          docs = files
            .filter((file) => file.indexing_status && file.indexing_status !== 'not_indexed')
            .map((file) => ({
              id: String(file.document_id),
              name: file.path,
              size: file.size,
              mime_type: file.mime_type,
              indexing_status: file.indexing_status ?? undefined,
            }));
          break;
        }
      }

      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      notify.error(t('documents.explorer.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ total: files.length, completed: 0 });
    let successCount = 0;
    let errorCount = 0;

    const uploadTasks = files.map(async (file) => {
      try {
        let uploadedDoc: DocumentRecord;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder_path', folderPath);

        switch (context.type) {
          case 'domain':
            uploadedDoc = await documentService.uploadToDomain(context.id, formData);
            break;
          case 'use_case':
            uploadedDoc = await documentService.uploadToAudit(context.id, formData);
            break;
          case 'cadrage':
            uploadedDoc = await documentService.uploadToCadrage(context.id, formData);
            break;
          case 'custom_folder':
            uploadedDoc = await documentService.uploadToCustomFolder(context.id, file);
            break;
          default:
            throw new Error('Ce type de contexte ne supporte pas l\'upload de documents');
        }

        setDocuments(prev => [uploadedDoc, ...prev]);
        return 'success' as const;
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        const detail = apiErrorDetail(error);
        return { status: 'error' as const, detail };
      } finally {
        setUploadProgress((prev) => ({
          ...prev,
          completed: Math.min(prev.total, prev.completed + 1),
        }));
      }
    });

    const uploadResults = await Promise.all(uploadTasks);
    successCount = uploadResults.filter((result) => result === 'success').length;
    errorCount = uploadResults.length - successCount;
    const firstDetail = uploadResults.find(
      (result): result is { status: 'error'; detail: string } =>
        typeof result === 'object' && result.status === 'error' && !!result.detail
    )?.detail;

    setUploading(false);
    setUploadProgress((prev) => ({ ...prev, completed: prev.total }));

    if (successCount > 0) {
      notify.success(t('documents.explorer.uploadSuccess', { count: successCount }));
      onDocumentsChange?.();
    }
    if (errorCount > 0) {
      notify.error(firstDetail || t('documents.explorer.uploadError', { count: errorCount }));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    e.target.value = ''; // Reset input
    void handleFileUpload(selectedFiles);
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm(t('documents.explorer.deleteConfirm'))) {
      return;
    }

    try {
      await documentService.delete(docId);
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
      notify.success(t('documents.explorer.deleteSuccess'));
      onDocumentsChange?.();
    } catch (error) {
      console.error('Error deleting document:', error);
      notify.error(t('documents.explorer.deleteError'));
    }
  };

  const handleDocumentVisibilityChange = (docId: string, visibility: DocumentVisibility) => {
    setDocuments(prev => prev.map(doc => (doc.id === docId ? { ...doc, visibility } : doc)));
  };

  const handleIndexDocument = async (docId: string) => {
    try {
      setIndexingDoc(docId);
      const result = await indexingService.indexDocument(docId, true);

      if (result.status === 'already_indexed') {
        notify.info(t('documents.explorer.alreadyIndexed'));
      } else if (result.status === 'indexing_in_progress') {
        notify.info(t('documents.explorer.indexingInProgress'));
      } else if (result.status === 'indexing_started') {
        notify.show({
          title: t('documents.explorer.infoTitle'),
          description: t('documents.explorer.indexingStartedDescription'),
          variant: 'info',
        });
      } else {
        notify.info(result.message || t('documents.explorer.indexingStarted'));
      }

      // Refresh quickly so the status badge can move to "En cours".
      loadDocuments();
      setTimeout(() => {
        void loadDocuments();
      }, 2000);
      onDocumentsChange?.();
    } catch (error) {
      console.error('Error indexing document:', error);
      notify.error(t('documents.explorer.indexError'));
    } finally {
      setIndexingDoc(null);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);

      // Filter by context for all types including custom folders
      const contextFilters = [
        {
          type: context.type,
          id: context.id,
        },
      ];

      const response = await indexingService.searchEmbeddings(searchQuery, contextFilters);

      // Transform results to match expected format
      const transformedResults = response.results.map((r) => ({
        id: r.chunk_id,
        content: r.text,
        score: r.score,
        document_name: r.document_name,
      }));

      setSearchResults(transformedResults);
    } catch (error) {
      console.error('Error searching:', error);
      notify.error(t('documents.explorer.searchError'));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleDownloadDocument = async (doc: DocumentRecord) => {
    if (!doc.id) {
      notify.error(t('documents.explorer.downloadMissingId'));
      return;
    }

    try {
      setDownloadingDocId(doc.id);
      const blob = isProjectContext
        ? await projectService.fileContent(context.id, Number(doc.id))
        : await documentService.downloadDocument(documentService.getStreamPathByDocumentId(doc.id, 'attachment'));
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.name?.split('/').pop() || 'document';
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading document:', error);
      notify.error(t('documents.explorer.downloadError'));
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleIndexAll = async () => {
    try {
      setIndexingAll(true);

      await indexingService.indexContext(context.type, context.id);

      notify.success(t('documents.explorer.indexAllSuccess'));

      // Reload documents to get updated indexing status
      setTimeout(() => {
        loadDocuments();
      }, 2000);

      // Notify parent to reload indexing tasks if callback exists
      if (onDocumentsChange) {
        onDocumentsChange();
      }
    } catch (error) {
      console.error('Error indexing all documents:', error);
      notify.error(t('documents.explorer.indexAllError'));
    } finally {
      setIndexingAll(false);
    }
  };

  const getContextPath = () => {
    return context.path.join(' / ');
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getIndexingStatus = (doc: DocumentRecord) => {
    // Mock status - replace with actual status from backend
    const status = doc.indexing_status || 'pending';
    return status as 'indexed' | 'pending' | 'indexing' | 'failed';
  };

  const getStatusBadge = (status: 'indexed' | 'pending' | 'indexing' | 'failed') => {
    switch (status) {
      case 'indexed':
        return (
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-3 w-3" />
            <span className="text-xs">{t('documents.status.indexed')}</span>
          </div>
        );
      case 'indexing':
        return (
          <div className="flex items-center gap-1 text-blue-600">
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span className="text-xs">{t('documents.status.indexing')}</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center gap-1 text-red-600">
            <XCircle className="h-3 w-3" />
            <span className="text-xs">{t('documents.status.failed')}</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1 text-orange-600">
            <Clock className="h-3 w-3" />
            <span className="text-xs">{t('documents.status.pending')}</span>
          </div>
        );
    }
  };

  const getChunksCount = (doc: DocumentRecord): number => {
    const raw = doc.chunks_count;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
  };

  const getIndexedItemsCount = (doc: DocumentRecord): number => {
    const candidates = [
      doc.indexed_items_count,
      doc.indexed_items,
      doc.items_indexed_count,
      doc.unique_entities,
      doc.entities_count,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
    }

    return 0;
  };

  const getThemeChips = (doc: DocumentRecord): string[] => {
    const allCandidates = [doc.theme_codes, doc.themes, doc.related_themes];
    const values: string[] = [];

    allCandidates.forEach((candidate) => {
      if (Array.isArray(candidate)) {
        candidate.forEach((item) => {
          if (typeof item === 'string' && item.trim()) {
            values.push(item.trim());
          }
        });
      }
    });

    return [...new Set(values)];
  };

  const isCustomFolderContext = context.type === 'custom_folder';
  // Default (system) folders: members only read; uploads and deletions are for org admins.
  const isSystemFolder = isCustomFolderContext && !!customFolder?.system_key;
  const isFolderReadOnly = isSystemFolder && !isOrganizationAdmin;
  // Folder documents follow their folder's sharing: no per-document switch there.
  const hasPerDocumentVisibility =
    context.type === 'domain' || context.type === 'use_case' || context.type === 'cadrage';
  const canUpload = (hasPerDocumentVisibility || context.type === 'custom_folder') && !isFolderReadOnly;
  const uploadPercent = uploadProgress.total > 0 ? Math.round((uploadProgress.completed / uploadProgress.total) * 100) : 0;

  return (
    <div className="bg-card border rounded-lg h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">{t('documents.explorer.title')}</h2>
          <div className="flex items-center gap-2">
            {canUpload && (
              <>
                <label htmlFor="file-input-docs-header">
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-input-docs-header')?.click()}
                    size="sm"
                    disabled={uploading}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    {uploading ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </label>
                <input
                  id="file-input-docs-header"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileInputChange}
                  accept={acceptedFormatsLabel}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleIndexAll}
                  disabled={indexingAll || (documents.length === 0 && subfolders.length === 0)}
                  title={t('knowledgeBasePage.actions.indexCustomFolder')}
                >
                  {indexingAll ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {folderPath !== ROOT_FOLDER_PATH && onOpenFolder && (
            <button
              type="button"
              onClick={() => onOpenFolder(parentFolderPath(folderPath))}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-accent rounded"
              title={t('documents.explorer.goUp')}
              aria-label={t('documents.explorer.goUp')}
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          )}
          <p className="text-sm text-muted-foreground">
            {getContextPath()}
          </p>
        </div>
        {isProjectContext && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {project?.is_private ? (
              <PrivateBadge tooltip={t('knowledgeBasePage.projects.privateTooltip')} />
            ) : (
              <SharedBadge tooltip={t('knowledgeBasePage.projects.sharedTooltip')} />
            )}
            <span className="text-xs text-muted-foreground">{t('knowledgeBasePage.projects.managedFromProject')}</span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => navigate(`/projets/${context.id}`)}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              {t('knowledgeBasePage.projects.open')}
            </Button>
          </div>
        )}
        {isCustomFolderContext && customFolder && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {isSystemFolder ? (
              <DefaultFolderBadge />
            ) : customFolder.shared_with_org ? (
              <SharedBadge tooltip={t('knowledgeSharing.badges.sharedFolderTooltip')} />
            ) : (
              <PrivateBadge tooltip={t('knowledgeSharing.badges.privateFolderTooltip')} />
            )}
            <span className="text-xs text-muted-foreground">
              {isFolderReadOnly
                ? t('knowledgeSharing.folder.adminUploadOnly')
                : t('knowledgeSharing.folder.documentsFollowFolder')}
            </span>
          </div>
        )}
        {uploading && uploadProgress.total > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>{t('documents.explorer.uploading')}</span>
              <span>{uploadProgress.completed}/{uploadProgress.total}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded overflow-hidden">
              <div
                className="h-full bg-orange-500 transition-all duration-200"
                style={{ width: `${uploadPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Right panel tabs */}
      <div className="px-4 pt-4 border-b">
        <div className="flex border-b -mb-px">
          <button
            onClick={() => setActiveViewTab('documents')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeViewTab === 'documents'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('documents.explorer.tabDocuments')}
          </button>
          <button
            onClick={() => setActiveViewTab('search')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeViewTab === 'search'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('documents.explorer.tabSearch')}
          </button>
        </div>
      </div>

      {activeViewTab === 'search' ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4 border-b bg-muted/30">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('documents.explorer.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                size="sm"
              >
                {searching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span className="ml-2">{t('documents.explorer.searchButton')}</span>
              </Button>
            </div>
          </div>

          <div className="p-4">
            {searchResults.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('documents.explorer.resultsCount', { count: searchResults.length })}
                </p>
                {searchResults.map((result, idx) => (
                  <div key={idx} className="p-3 bg-card border rounded-md text-sm">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-primary">{result.document_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('documents.explorer.score', { score: result.score.toFixed(2) })}
                      </span>
                    </div>
                    <p className="text-muted-foreground line-clamp-3">{result.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                {t('documents.explorer.searchEmpty')}
              </div>
            )}
          </div>
        </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-muted-foreground">{t('documents.explorer.loading')}</p>
              </div>
            ) : documents.length === 0 && subfolders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">{t('documents.explorer.empty')}</p>
                {canUpload && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('documents.explorer.emptyUploadHint')}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {subfolders.map((folder) => (
                  <button
                    key={folder.path}
                    type="button"
                    onClick={() => onOpenFolder?.(folder.path)}
                    className="w-full p-4 border rounded-lg hover:bg-accent/50 transition-colors flex items-center gap-3 text-left"
                  >
                    <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <span className="font-medium truncate flex-1">{folder.name}</span>
                    <span
                      className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0"
                      title={t('documents.explorer.folderDocuments', { count: folder.documentCount })}
                    >
                      {folder.documentCount}
                    </span>
                  </button>
                ))}
                {documents.map((doc) => {
                  const indexingStatus = getIndexingStatus(doc);
                  const chunkCount = getChunksCount(doc);
                  const indexedItemsCount = getIndexedItemsCount(doc);
                  const themeChips = getThemeChips(doc);
                  const visibleThemes = themeChips.slice(0, MAX_THEME_CHIPS);
                  const hasMoreThemes = themeChips.length > MAX_THEME_CHIPS;

                  return (
                    <div
                      key={doc.id}
                      className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <FileText className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{doc.name}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{formatBytes(doc.size || 0)}</span>
                              <span>•</span>
                              {!isProjectContext && (
                                <>
                                  <span>
                                    {doc.created_at
                                      ? formatDistanceToNow(new Date(doc.created_at), {
                                          addSuffix: true,
                                          locale: dateLocale,
                                        })
                                      : t('notes.unknownDate')}
                                  </span>
                                  <span>•</span>
                                </>
                              )}
                              {getStatusBadge(indexingStatus)}
                            </div>
                            {hasPerDocumentVisibility && (
                              <DocumentVisibilityControl
                                className="mt-2"
                                documentId={doc.id}
                                visibility={doc.visibility}
                                ownerId={doc.owner_id}
                                onVisibilityChange={handleDocumentVisibilityChange}
                              />
                            )}
                            {indexingStatus === 'indexed' && !isProjectContext && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 px-2 py-0.5 text-[11px] font-medium">
                                  {t('documents.explorer.chunksCount', { count: chunkCount })}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-medium">
                                  {t('documents.explorer.indexedItemsCount', { count: indexedItemsCount })}
                                </span>
                                {visibleThemes.map((theme) => (
                                  <span
                                    key={`${doc.id}-${theme}`}
                                    className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300 px-2 py-0.5 text-[11px] font-medium"
                                    title={theme}
                                  >
                                    {theme}
                                  </span>
                                ))}
                                {hasMoreThemes && (
                                  <span
                                    className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px] font-medium"
                                    title={themeChips.slice(MAX_THEME_CHIPS).join(', ')}
                                  >
                                    ...
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {!isProjectContext && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleIndexDocument(doc.id)}
                              disabled={indexingDoc === doc.id || indexingStatus === 'indexing'}
                              title={t('documents.explorer.indexTitle')}
                            >
                              {indexingDoc === doc.id ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDocument(doc)}
                            disabled={downloadingDocId === doc.id}
                            title={t('documents.explorer.downloadTitle')}
                          >
                            {downloadingDocId === doc.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                          {!isFolderReadOnly && !isProjectContext && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              title={t('documentList.delete')}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {canUpload && (
              <p className="text-xs text-muted-foreground mt-3">
                {t('knowledgeBasePage.documents.acceptedFormats', {
                  formats: ACCEPTED_FORMAT_EXTENSIONS.map((ext) => ext.replace('.', '').toUpperCase()).join(', '),
                })}
              </p>
            )}
            {canUpload && folderPath !== ROOT_FOLDER_PATH && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('documents.explorer.uploadTarget', { path: folderPath })}
              </p>
            )}
          </div>
        )}
    </div>
  );
};
